<template>
  <div class="hmt-map-frame">
    <div v-if="isMap" :style="{ height: '100%' }">
      <div id="map-frame__google-maps"></div>
      <button 
        class="hmt-map-frame__streetview-btn"
        :class="{ 'hmt-map-frame__streetview-btn--invert': visibleStreetView }"
        @click="toggleStreetView"
      >
        <span v-if="visibleStreetView">Show Map View <FontAwesomeIcon icon="map-marked-alt" size="lg" /></span>
        <span v-else>Show Street View <FontAwesomeIcon icon="street-view" size="lg" /></span>
      </button>
    </div>
    <div 
      v-else
      class="hmt-map-frame__cover"
      :style="{ backgroundImage: `url(${coverImage})` }"
    >
      <slot></slot>
      <button 
        v-if="!disabledButton"
        class="hmt-map-frame__cover-btn" 
        :style="{backgroundColor: getButtonColor, borderColor: getButtonColor}"
        @click="showMap"
      >
        View Map <FontAwesomeIcon icon="map-marked-alt" size="lg" />
      </button>
    </div>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex';


  import tytryStyles from './styles/tytry.json';
  import tytryImage from './images/tytry-cover.png';
  import config from '@/config';

  let initGoogleMaps = false;

  const MAP_ID = 'map-frame__google-maps';
  const CUSTOM_MARKER_SVG = '<svg version="1.1" xmlns="http://www.w3.org/2000/svg" xml:space="preserve" x="0px" y="0px" height="60" width="48" viewBox="0 0 165.411 207.46001">' +
    '<g><path d="m 165.411,0 0,182.477 -57.719,0 L 82.706,207.46 57.72,182.477 0,182.477 0,0 165.411,0 Z" fill="#ee4e5e" />' +
    '<path d="m 135.59003,72.306606 0,62.611994 -23.05186,0 0,-49.850868 -29.832369,-18.741733 -29.83179,18.741733 0,49.850868 -23.05305,0 0,-62.611994 52.82308,-33.169779 52.945989,33.169779 z" fill="#ffffff" /></g></svg>';


  export default {
    name: 'HMTMapFrame',
    data () {
      return {
        isMap: false,
        coverImage: tytryImage,
        mapId: MAP_ID,
        mapEl: null,
        panorama: null,
        styles: tytryStyles,
        fullscreenControl: true,
        zoomControl: true,
        mapTypeControl: true,
        visibleStreetView: false
      }
    },
    props: {
      disabledButton: {
        type: Boolean,
        default: false
      }
    },
    computed: {
      ...mapGetters('tour', ['getMap', 'getButtonColor']),
      ...mapGetters('ui', ['getViewMap']),
      vmp () {
        return this.getViewMap;
      }
    },
    methods: {
      createGoogleMaps () {
        return new Promise((resolve, reject) => {
          let map = document.createElement('script');
          map.src = config.GOOGLE_MAPS_URL;
          map.type = 'text/javascript';
          map.onload = resolve;
          map.onerror = reject;
          document.head.appendChild(map);
          initGoogleMaps = true;
        });
      },
      initGoogleMaps () {
        const { lat, lng, zoom } = this.getMap;

        const location = { lat, lng };

        const mapOptions = {
          center: location,
          zoom,
          disableDefaultUI: true,
          fullscreenControl: this.fullscreenControl,
          zoomControl: this.zoomControl,
          fullscreenControlOptions: {
            position: google.maps.ControlPosition.RIGHT_BOTTOM
          },
          styles: this.styles
        };

        const streetViewOptions = {
          position: location,
          addressControlOptions: {
            position: google.maps.ControlPosition.RIGHT_BOTTOM
          },
          fullscreenControlOptions: {
            position: google.maps.ControlPosition.RIGHT_BOTTOM
          },
          visible: this.visibleStreetView,
          linksControl: false,
          enableCloseButton: false
        };

        this.mapEl = new google.maps.Map(document.getElementById(this.mapId), mapOptions);
        this.panorama = new google.maps.StreetViewPanorama(document.getElementById(this.mapId), streetViewOptions);

        const image = {
          url: 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(CUSTOM_MARKER_SVG),
          size: new google.maps.Size(48, 60)
        };

        new google.maps.Marker({
          map: this.mapEl,
          position: location,
          icon: image
        });
      },
      googleMapsFailedToLoad () {
        console.log('Google Maps failed to load.');
      },
      toggleStreetView () {
        this.visibleStreetView = !this.visibleStreetView;
        this.panorama.setVisible(this.visibleStreetView);
      },
      showMap () {
        this.isMap = true;

        if (!initGoogleMaps) {
          this.createGoogleMaps().then(this.initGoogleMaps, this.googleMapsFailedToLoad);
        } else {
          this.initGoogleMaps();
        }
      }
    },
    watch: {
      vmp () {
        if (!initGoogleMaps) {
          this.showMap();
        }
      }
    }
  };
</script>

<style lang="scss">
  #map-frame__google-maps {
    width: 100%;
    height: 100%;
  }

  .hmt-map-frame {
    position: relative;
    width: 100%;
    height: 100%;
    min-height: 100px;

    &__cover {
      display: flex;
      justify-content: center;
      align-items: center;
      height: 100%;
      width: 100%;
      background-repeat: no-repeat;
      background-size: cover;
      background-position: 50% 50%;
    }

    &__cover-btn {
      padding: 30px 40px;
      border: none;
      border-radius: 2px;
      background-color: #333;
      box-shadow: rgba(0, 0, 0, 0.3) 0px 1px 4px -1px;
      font-size: 16px;
      line-height: 1;
      color: #fff;
      text-transform: uppercase;
      outline: none;
      cursor: pointer;
      transition: background-color .2s ease-out;

      svg {
        position: relative;
        top: -1px;
        display: inline-block;
        margin-left: 10px;
      }
    }

    &__streetview-btn {
      position: absolute;
      top: 15px;
      left: 10px;
      z-index: 5;
      padding: 12px 20px;
      border: none;
      border-radius: 2px;
      background-color: #fff;
      font-size: 12px;
      font-weight: 500;
      color: #666;
      text-transform: uppercase;
      box-shadow: rgba(0, 0, 0, 0.3) 0px 1px 4px -1px;
      line-height: 1;
      cursor: pointer;
      transition: color .2s ease-out;

      svg {
        transition: color .2s ease-out;
      }

      &:hover {
        color: #000;
      }

      svg {
        position: relative;
        top: 0;
        margin-left: 7px;
      }
    }

    &__streetview-btn--invert {
      background-color: #222;
      color: #888;

      &:hover {
        color: #b1b1b1;
      }
    }
  }
</style>
